import { Button } from '@daily/shared/components/Button';
import { Card, CardContent } from '@daily/shared/components/Card';
import { Text } from '@daily/shared/components/Text';
import { useTheme } from '@daily/shared/contexts/Theme';
import { useLocalSessionId } from '@daily-co/daily-react';
import classNames from 'classnames';
import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useBreakoutSessionState } from '/components/BreakoutRooms/hooks/useBreakoutSessionState';
import { useBreakouts } from '/contexts/BreakoutsProvider';
import { SIDEBAR_WIDTH } from '/lib/constants';
import { useIsMobile, useSidebarView } from '/lib/state/core';
import { useMobileTray } from '/lib/state/layout';

import { useCountdown } from './useCountdown';

export const BreakoutCountdown: React.FC = memo(() => {
  const { t } = useTranslation();
  const { colors, zIndex } = useTheme();
  const { breakoutHost, endDate } = useBreakoutSessionState();
  const { abortEndingBreakouts } = useBreakouts();
  const [sidebarView] = useSidebarView();
  const [isMobile] = useIsMobile();
  const localSessionId = useLocalSessionId();

  const isBreakoutHost = localSessionId === breakoutHost;

  const countdown = useCountdown(endDate);
  const hasCountdown = useMemo(() => Boolean(countdown), [countdown]);
  const [, setShowMobileTray] = useMobileTray();

  useEffect(() => {
    if (!hasCountdown) return;
    setShowMobileTray(true);
  }, [hasCountdown, setShowMobileTray]);

  if (!countdown) return null;

  return (
    <div className={classNames('breakout-countdown', { isMobile })}>
      <Card noBorder spacing={8}>
        <CardContent>
          <Text color="white" variant="strong">
            {t('breakouts.countdown', {
              countdown,
            })}
          </Text>
          {isBreakoutHost && (
            <Button
              variant="ghost"
              onClick={abortEndingBreakouts}
              className="abort-button"
            >
              {t('general.cancel')}
            </Button>
          )}
        </CardContent>
      </Card>
      <style jsx>{`
        .breakout-countdown :global(.card) {
          font-variant-numeric: tabular-nums;
          height: auto;
          position: fixed;
          left: 50%;
          margin-left: ${sidebarView ? Number(SIDEBAR_WIDTH) / -2 : 0}px;
          top: calc(var(--header-height) + 8px);
          transform: translateX(-50%);
          width: auto;
          z-index: ${zIndex.snackbar};
        }
        .breakout-countdown:not(.isMobile) :global(.card-content) {
          background: ${colors.system.red};
          display: flex;
          gap: 8px;
        }
        .breakout-countdown.isMobile :global(.card) {
          background: transparent;
          left: auto;
          margin-left: 0;
          position: relative;
          top: auto;
          transform: none;
          white-space: nowrap;
          z-index: auto;
        }
        .breakout-countdown.isMobile :global(.card-content) {
          background: transparent;
        }
        .breakout-countdown :global(.abort-button) {
          color: ${colors.system.white};
          text-decoration: underline;
        }
        .breakout-countdown :global(.abort-button:not(.loading)):hover,
        .breakout-countdown :global(.abort-button:not(.loading)):focus-visible {
          box-shadow: 0 0 0 2px ${colors.system.white};
        }
      `}</style>
    </div>
  );
});
BreakoutCountdown.displayName = 'BreakoutCountdown';
